/**
 * TripOrderAssignmentPanel — link or unlink orders from a trip (trip detail Orders tab).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, LinkOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import OrderSearchAutocomplete from "@/shared/components/OrderSearchAutocomplete.jsx";
import { getOrders } from "../../orders/services/orderService.js";
import {
  addOrderToTrip,
  removeOrderFromTrip,
} from "../services/tripService.js";
import { logError } from "@/shared/utils/logger.js";
import {
  corridorsMatch,
  formatCorridorDisplay,
} from "@/shared/utils/routeCorridorUtils.js";

import { LINKABLE_TRIP_STATUSES } from "../constants/tripStatuses.js";

const normalizeOrder = (order) => {
  if (typeof order === "string") {
    return { id: order, data: null };
  }
  return { id: order?.id, data: order };
};

/**
 * @param {Object} props
 * @param {Object} props.trip
 * @param {function} [props.onOrdersChanged]
 */
const TripOrderAssignmentPanel = ({ trip, onOrdersChanged }) => {
  const navigate = useNavigate();
  const tripId = trip?.id;
  const linkedOrders = trip?.orders || [];
  const canLinkOrders =
    LINKABLE_TRIP_STATUSES.has(trip?.status) &&
    (trip?.status === "loaded" ||
      trip?.status === "en_route" ||
      trip?.bookingOpen !== false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [linkingOrderId, setLinkingOrderId] = useState(null);
  const [unlinkingOrderId, setUnlinkingOrderId] = useState(null);
  const [error, setError] = useState(null);
  const [matchRouteOnly, setMatchRouteOnly] = useState(true);

  const tripRouteLabel =
    formatCorridorDisplay(trip?.routeCorridor) || trip?.routeCorridor;

  const linkedOrderIds = useMemo(
    () =>
      new Set(
        linkedOrders
          .map((order) => (typeof order === "string" ? order : order?.id))
          .filter(Boolean)
      ),
    [linkedOrders]
  );

  const fetchAvailableOrders = useCallback(async () => {
    if (!tripId) return;
    setLoadingOrders(true);
    setError(null);
    try {
      const response = await getOrders({ status: "pending" });
      const orders = response.data || [];
      const unassigned = orders.filter((order) => !order.tripId);
      setAvailableOrders(unassigned);
    } catch (err) {
      logError("TripOrderAssignmentPanel: failed to load orders", {
        tripId,
        error: err?.message,
      });
      setError(err?.message || "Failed to load orders");
      setAvailableOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (modalOpen) {
      setSelectedOrder(null);
      setError(null);
      fetchAvailableOrders();
    }
  }, [modalOpen, fetchAvailableOrders]);

  const visibleOrders = useMemo(() => {
    return availableOrders.filter((order) => {
      if (linkedOrderIds.has(order.id)) return false;
      if (matchRouteOnly && trip?.routeCorridor) {
        return corridorsMatch(order.routeCorridor, trip.routeCorridor);
      }
      return true;
    });
  }, [availableOrders, linkedOrderIds, matchRouteOnly, trip?.routeCorridor]);

  const handleOpenModal = () => {
    if (!canLinkOrders) return;
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
    setError(null);
  };

  const handleLinkOrder = async (orderId) => {
    if (!tripId || !orderId || linkedOrderIds.has(orderId)) return;

    setLinkingOrderId(orderId);
    setError(null);
    try {
      await addOrderToTrip(tripId, orderId);
      handleCloseModal();
      onOrdersChanged?.();
    } catch (err) {
      setError(err?.message || "Failed to link order to trip");
    } finally {
      setLinkingOrderId(null);
    }
  };

  const handleUnlinkOrder = async (orderId) => {
    if (!tripId || !orderId) return;

    setUnlinkingOrderId(orderId);
    setError(null);
    try {
      await removeOrderFromTrip(tripId, orderId);
      onOrdersChanged?.();
    } catch (err) {
      setError(err?.message || "Failed to unlink order from trip");
    } finally {
      setUnlinkingOrderId(null);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Typography variant="h6">
          Orders ({linkedOrders.length})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          disabled={!canLinkOrders}
          onClick={handleOpenModal}
          sx={{ textTransform: "none" }}
        >
          Link Order
        </Button>
      </Box>

      {!canLinkOrders ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Orders cannot be linked or unlinked on completed trips.
        </Alert>
      ) : null}

      {error && !modalOpen ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {linkedOrders.length > 0 ? (
        <Grid container spacing={2}>
          {linkedOrders.map((order, index) => {
            const { id: orderId, data: orderData } = normalizeOrder(order);

            return (
              <Grid item xs={12} sm={6} md={4} key={orderId || index}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: orderData ? "pointer" : "default",
                    "&:hover": orderData
                      ? {
                          boxShadow: 2,
                        }
                      : {},
                  }}
                  onClick={() => {
                    if (orderData?.id) {
                      navigate(`/orders/${orderData.id}`);
                    }
                  }}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography variant="subtitle1" fontWeight="medium">
                        Order {orderId}
                      </Typography>
                      {canLinkOrders ? (
                        <Tooltip title="Unlink order from trip">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={unlinkingOrderId === orderId}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleUnlinkOrder(orderId);
                            }}
                          >
                            <LinkOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                    {orderData ? (
                      <Box mt={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Customer: {orderData.customer || "N/A"}
                        </Typography>
                        {orderData.pickup?.address ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            From: {orderData.pickup.address}
                          </Typography>
                        ) : null}
                        {orderData.delivery?.address ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            To: {orderData.delivery.address}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Alert
          severity="info"
          action={
            canLinkOrders ? (
              <Button color="inherit" size="small" onClick={handleOpenModal}>
                Link Order
              </Button>
            ) : null
          }
        >
          No orders assigned to this trip.
          {canLinkOrders && tripRouteLabel
            ? ` Link a pending order matching ${tripRouteLabel}.`
            : canLinkOrders
              ? " Link a pending order to add cargo to this run."
              : ""}
        </Alert>
      )}

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Link order to trip</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for an order or pick from eligible pending orders not yet
            assigned to a trip.
          </Typography>

          <OrderSearchAutocomplete
            value={selectedOrder}
            onChange={setSelectedOrder}
            label="Search order to link"
            restrictToCustomer={false}
          />

          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button
              variant="contained"
              size="small"
              disabled={!selectedOrder?.id || linkingOrderId === selectedOrder?.id}
              onClick={() => handleLinkOrder(selectedOrder.id)}
              sx={{ textTransform: "none" }}
            >
              {linkingOrderId === selectedOrder?.id ? "Linking…" : "Link selected order"}
            </Button>
          </Box>

          {trip?.routeCorridor ? (
            <FormControlLabel
              control={
                <Switch
                  checked={matchRouteOnly}
                  onChange={(event) => setMatchRouteOnly(event.target.checked)}
                />
              }
              label={`Only show matching ${tripRouteLabel} orders`}
              sx={{ mt: 2, mb: 1 }}
            />
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          ) : null}

          {loadingOrders ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No eligible orders found.
                          {matchRouteOnly && trip?.routeCorridor
                            ? " Try showing all corridors or search by order ID."
                            : " Try searching by order ID above."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{order.id}</TableCell>
                        <TableCell>{order.customer || "—"}</TableCell>
                        <TableCell>
                          {formatCorridorDisplay(order.routeCorridor) ||
                            order.routeCorridor ||
                            "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={linkingOrderId === order.id}
                            onClick={() => handleLinkOrder(order.id)}
                            sx={{ textTransform: "none" }}
                          >
                            {linkingOrderId === order.id ? "Linking…" : "Link"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripOrderAssignmentPanel;
